import { CampaignLabelOptions } from '@/entities/campaign';
import { getPlatformName, PLATFORM_LOGO_BY_TYPE } from '@/entities/public-filter';
import { ProfileImage } from '@/shared/components/profile-image';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { TableCell, TableRow } from '@/shared/components/shadcn/table';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { Copy } from 'lucide-react';
import { EMPTY_LABEL_VALUE } from '../../detail-constants';
import type { CreatorRow } from '../../hooks';

export function CreatorTableRow({
  canEdit,
  creator,
  onChangeLabel,
  onCopyEmail,
}: {
  canEdit: boolean;
  creator: CreatorRow;
  onChangeLabel: (creatorId: string, label: string) => Promise<void>;
  onCopyEmail: (email: string) => void;
}) {
  const PlatformLogo = PLATFORM_LOGO_BY_TYPE[creator.platform];

  const handleChangeLabel = async (value: string) => {
    await onChangeLabel(creator.id, value === EMPTY_LABEL_VALUE ? '' : value);
  };

  return (
    <TableRow
      gridTemplateColumns="minmax(240px,1.35fr) minmax(240px,1.35fr) minmax(156px,0.9fr) minmax(96px,0.55fr)"
      className={creator.isMissing ? 'bg-muted/70' : undefined}
    >
      <TableCell className="items-start">
        <div className="flex items-center gap-3">
          <ProfileImage
            className="size-10 rounded-full object-cover"
            src={creator.profile_image_url ?? ''}
            name={creator.username}
          />
          <div className="flex-1">
            <div className="flex w-full items-center gap-1">
              <span className="text-foreground min-w-0 truncate font-semibold">
                {creator.nickname}
              </span>
              <span
                className="text-muted-foreground shrink-0"
                title={getPlatformName(creator.platform)}
                aria-label={getPlatformName(creator.platform)}
              >
                <PlatformLogo className="size-4" />
              </span>
            </div>
            <div className="text-muted-foreground text-sm">
              <span className="truncate">@{creator.username}</span>
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {creator.email ? (
          <div className="text-muted-foreground flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0 truncate" title={creator.email}>
              {creator.email}
            </span>
            <TooltipFlat asChild side="top" content="Copy email">
              <Button
                variant="ghost"
                size="icon"
                className="size-8 p-0"
                onClick={() => onCopyEmail(creator.email ?? '')}
                aria-label={`Copy email for ${creator.nickname}`}
              >
                <Copy className="size-3.5" />
              </Button>
            </TooltipFlat>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">No email yet</span>
        )}
      </TableCell>
      <TableCell>
        <Select
          key={`${creator.id}:${creator.label ?? EMPTY_LABEL_VALUE}`}
          defaultValue={creator.label || EMPTY_LABEL_VALUE}
          onValueChange={(value) => {
            void handleChangeLabel(value);
          }}
          disabled={!canEdit}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select label" />
          </SelectTrigger>
          <SelectContent>
            {CampaignLabelOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        {creator.isMissing ? (
          <Badge variant="outline" className="border-destructive/30 text-destructive">
            0 posts
          </Badge>
        ) : (
          <Badge variant="secondary">{creator.post_count} posts</Badge>
        )}
      </TableCell>
    </TableRow>
  );
}
